<template>
  <div :class="wrapperClasses">
    <div class="listing">
      <b-table :data="collectionData">
        <template slot-scope="props">
          <b-table-column field="collectionName" label="Collection" sortable>{{props.row.name}}</b-table-column>
          <b-table-column field="json" class="json-export" label="JSON" sortable>
            <a v-if="props.row.json" href="#" @click.prevent="generateExport(props.row.key)">
              <b-icon icon="cloud-braces" size="is-small" />
            </a>
            <a
              v-if="props.row.hasFile && props.row.json"
              :href="props.row.file.name"
              class="file-info"
              :title="props.row.file.modified|shortDatetime"
              download
            >{{props.row.file.size|fileSize}}</a>
          </b-table-column>
          <b-table-column field="csv" label="CSV download">
            <template v-if="props.row.csv">
              <a
                v-if="props.row.hasCsv"
                :href="props.row.csvFile.name"
                title="Download CSV"
                download
              >
                <b-icon icon="file-delimited-outline" size="is-small" />
              </a>
              <span
                v-if="props.row.hasCsv"
                class="trigger update"
                @click="createCsv(props.row)"
                title="Update CSV download"
              >
                <b-icon icon="refresh" size="is-small" />
                <time>{{props.row.csvFile.modified|shortDatetime}}</time>
              </span>
              <span v-else class="trigger create" @click="createCsv(props.row)">
                <b-icon icon="file-delimited-outline" size="is-small" />
              </span>
            </template>
          </b-table-column>
        </template>
      </b-table>
      <b-table :data="fileRows">
        <template slot-scope="props">
          <b-table-column field="label" label="Category" sortable>{{props.row.label}}</b-table-column>
          <b-table-column field="num" class="num" label="#" sortable>{{props.row.num}}</b-table-column>
          <b-table-column field="size" label="Total file usage">{{props.row.usage|fileSize}}</b-table-column>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { exportCollection, readDirectory, getData } from "../api/methods";
import { notEmptyString } from "../api/helpers";
import filters from "../mixins/filters";
import config from "../config/config";

export default {
  name: "OperationsPanel",
  components: {},
  mixins: [filters],
  data() {
    return {
      collections: [
        { key: "users", name: "Users", csv: true, json: true },
        { key: "submissions", name: "All Submissions", csv: true, json: true },
        {
          key: "submissions__valid",
          name: "Eligible Submissions",
          csv: true,
          json: false
        },
        {
          key: "submissions__paid_not_submitted",
          name: "Paid not submitted",
          csv: true,
          json: false
        },
        {
          key: "submissions__not_shortlisted",
          name: "Not pre-shortlisted",
          csv: true,
          json: false
        },
        {
          key: "submissions__shortlisted",
          name: "Return to (pre-shortlisted)",
          csv: true,
          json: false
        },
        {
          key: "submissions__selected",
          name: "Shortlisted",
          csv: true,
          json: false
        },
        { key: "responses", name: "Survey Responses", csv: true },
        {
          key: "responses__shortlisted",
          name: "Equal ops data of shortlisted candidates",
          csv: true,
          json: false
        },
        {
          key: "responses__selected",
          name: "Equal ops data of selected candidates",
          csv: true,
          json: false
        },
        {
          key: "responses__feedback",
          name: "Feedback",
          csv: true,
          json: false
        },
        { key: "colleges", name: "Colleges", csv: true, json: true },
        { key: "countries", name: "Countries", csv: true, json: true },
        { key: "settings", name: "Settings", csv: false, json: true },
        { key: "snippets", name: "Content snippets", csv: false, json: true },
        { key: "questions", name: "Survey Questions", csv: false, json: true }
      ],
      exportDir: {
        files: [],
        size: 0
      },
      mediaDir: {
        files: [],
        size: 0
      }
    };
  },
  computed: {
    wrapperClasses() {
      let cls = ["operations"];
      return cls;
    },
    collectionData() {
      const hasFiles = this.exportDir.files.length > 0;
      const mapFileData = file => {
        if (file instanceof Object) {
          if (file.modified) {
            file.mtime = file.modified;
          }
          return {
            name: "/exports/" + file.name,
            size: file.size,
            modified: file.mtime
          };
        }
      };
      const matchCsv = col => {
        const parts = col.key.split("__");
        let segments = [parts[0]];
        switch (segments[0]) {
          case "submissions":
          case "responses":
            segments.push("all");
            if (parts.length > 1) {
              if (notEmptyString(parts[1], 2)) {
                segments[1] = parts[1];
              }
            }
            break;
        }
        const tgName = segments.join("-") + ".csv";
        const file = this.exportDir.files.find(f => f.name === tgName);
        if (file) {
          if (file.size) {
            col.csvFile = mapFileData(file);
            col.hasCsv = file.size > 0;
          }
        }
      };

      const matchJson = col => {
        const tgName = col.key + ".json";
        const file = this.exportDir.files.find(f => f.name === tgName);
        if (file) {
          if (file.size) {
            col.file = mapFileData(file);
            col.hasFile = file.size > 0;
          }
        }
      };

      return this.collections.map(col => {
        col.file = {
          name: "",
          modified: "",
          size: 0
        };
        col.csvFile = {
          name: "",
          modified: "",
          size: 0
        };
        col.hasFile = false;
        col.hasCsv = false;
        if (col.csv && hasFiles) {
          col.csvGenLink = this.csvGenLink(col.key);
          matchCsv(col);
        }
        if (col.json && hasFiles) {
          matchJson(col);
        }
        return col;
      });
    },
    fileRows() {
      let rows = [];
      if (this.hasMediaDirInfo) {
        rows.push({
          label: "All media files",
          num: this.numMediaFiles,
          usage: this.mediaSize
        });
        const sourceFiles = this.mediaDir.files.filter(
          f => f.name.indexOf("-resize-") < 0
        );
        const sourceFileSize = sourceFiles
          .map(f => {
            let size = 0;
            if (f.size) {
              size = f.size;
            }
            return size;
          })
          .reduce((a, b) => a + b, 0);

        rows.push({
          label: "All source media files",
          num: sourceFiles.length,
          usage: sourceFileSize
        });
      }
      return rows;
    },
    hasMediaDirInfo() {
      return this.mediaDir.size > 100;
    },
    numMediaFiles() {
      return this.mediaDir.files.length;
    },
    mediaSize() {
      return this.mediaDir.size;
    }
  },
  created() {
    readDirectory("exports").then(data => {
      if (data.valid) {
        this.exportDir = {
          files: data.files,
          size: data.size
        };
      }
    });
    setTimeout(() => {
      readDirectory("media").then(data => {
        if (data.valid) {
          this.mediaDir = {
            files: data.files,
            size: data.size
          };
        }
      }, 1000);
    });
  },
  mounted() {
    //
  },
  filters: {
    jsonLink(val) {
      return (
        config.mediaPath.replace("/media/", "/api/") +
        "setting/download-export/" +
        val +
        "/json"
      );
    }
  },
  methods: {
    generateExport(collectionName) {
      exportCollection(collectionName, "json").then(json => {
        if (json.size) {
          this.updateExportFiles(json);
        }
      });
    },
    updateExportFiles(file) {
      let nf = file;
      if (file.filename) {
        nf = {
          name: file.filename,
          mtime: file.modified,
          size: file.size
        };
      }
      const currFileIndex = this.exportDir.files.findIndex(
        f => f.name === nf.name
      );
      if (currFileIndex < 0) {
        this.exportDir.files.push(nf);
      } else {
        this.exportDir.files[currFileIndex] = nf;
      }
    },
    createCsv(col) {
      getData(col.csvGenLink).then(data => {
        if (data.valid) {
          data.modified = new Date()
            .toISOString()
            .split(".")
            .shift();
          this.updateExportFiles(data);
        }
      });
    },
    csvGenLink(val) {
      let link = "";
      const parts = val.split("__");
      const category = parts.shift();
      const subcat = parts.length > 0 ? parts.shift() : "all";
      switch (category) {
        case "responses":
          link = "response/list-flat-csv";
          if (subcat) {
            link += "/" + subcat;
          }
          break;
        case "submissions":
          link = "submission/list-flat-csv/" + subcat;

          switch (subcat) {
            case "all":
            case "valid":
            case "paid_not_submitted":
              break;
            default:
              link += "/works";
              break;
          }
          break;
        default:
          link = "setting/download-export/" + val + "/csv";
          break;
      }
      return link;
    }
  }
};
</script>